/* Nuava — taies d'oreiller offertes avec les couettes : 2 par couette, dans la couleur de la couette.
   Produits à 0 € « 2 taies », « 4 taies » et « 6 taies » (une couette, Pack Duo, Pack familial).
   Garde le panier en accord avec les couettes : ajoute ce qui manque, retire ce qui n'a plus de couette,
   puis prévient le panier d'Horizon pour qu'il se redessine.
   Pendant la remise en ordre, le panier est figé (classe nv-taies-sync sur <html>) et un envoi vers le paiement
   est retenu puis relancé : les numéros de ligne changent quand une ligne de taies est remplacée.
   Les données viennent de #nuvea-taies-data (layout/theme.liquid). */

const SOURCE = 'nuvea-taies';
const data = (() => {
  try { return JSON.parse(document.getElementById('nuvea-taies-data').textContent); } catch (e) { return null; }
})();
const eventsModule = import('@shopify/events').catch(() => null);

/* Theme est une const globale d'Horizon (snippets/scripts.liquid) : elle n'est pas une propriété de window */
const routes = (typeof Theme !== 'undefined' && Theme && Theme.routes) || {};
const cartUrl = routes.cart_url || '/cart';
const updateUrl = routes.cart_update_url || '/cart/update.js';

/* Produits de taies pour n couettes d'une même couleur : « 6 taies » par groupe de 3 couettes, puis « 2 » ou « 4 » pour le reste */
function taiesLots(n) {
  const lots = [];
  for (let k = Math.floor(n / 3); k > 0; k--) lots.push('6');
  if (n % 3 === 1) lots.push('2');
  if (n % 3 === 2) lots.push('4');
  return lots;
}

/* Taies attendues, par variante : on compte les couettes de chaque couleur puis on choisit les produits 2 / 4 / 6 */
function wantedTaies(items) {
  const perColor = new Map();
  items.forEach((it) => {
    if (it.handle !== data.couette) return;
    const color = (it.options_with_values || []).map((o) => o.value).find((v) => data.variantes['2'][v]);
    if (color) perColor.set(color, (perColor.get(color) || 0) + it.quantity);
  });
  const wanted = new Map();
  perColor.forEach((n, color) => taiesLots(n).forEach((size) => {
    const id = String(data.variantes[size][color]);
    wanted.set(id, (wanted.get(id) || 0) + 1);
  }));
  return wanted;
}

async function sync() {
  const cart = await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
  const items = cart.items || [];
  const wanted = wantedTaies(items);
  const current = new Map();
  items.forEach((it) => {
    if (!data.taies.includes(it.handle)) return;
    const id = String(it.variant_id);
    current.set(id, (current.get(id) || 0) + it.quantity);
  });

  const updates = {};
  new Set([...wanted.keys(), ...current.keys()]).forEach((id) => {
    const qty = wanted.get(id) || 0;
    if ((current.get(id) || 0) !== qty) updates[id] = qty;
  });
  if (!Object.keys(updates).length) return;

  /* Le panier se redessine à partir de la réponse même, sans second aller-retour */
  const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId))];
  const body = { updates };
  if (sectionIds.length) {
    body.sections = sectionIds.join(',');
    body.sections_url = window.location.pathname;
  }

  const events = await eventsModule;
  const Ev = events && events.CartLinesUpdateEvent;
  let deferred = null;
  if (Ev) {
    deferred = Ev.createPromise();
    deferred.promise.catch(() => {});
    const ev = new Ev({
      action: 'update',
      context: 'cart',
      /* Seules les lignes déjà présentes ont une clé ; les nouvelles variantes n'en ont pas encore */
      lines: items.filter((it) => String(it.variant_id) in updates).map((it) => ({ id: it.key, quantity: updates[String(it.variant_id)] })),
      promise: deferred.promise,
    });
    ev.nuveaTaies = true;
    document.body.dispatchEvent(ev);
  }
  try {
    const response = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const updated = await response.json();
    if (!response.ok || updated.status) throw new Error(updated.description || 'cart');
    if (deferred) {
      deferred.resolve({
        cart: Ev.createCartFromAjaxResponse(updated),
        detail: { items: updated.items, itemCount: updated.item_count, sections: updated.sections || undefined, source: SOURCE, didError: false },
      });
    }
  } catch (error) {
    if (deferred) deferred.reject(error);
  }
}

/* ---------- File d'attente et verrou du panier ---------- */

let pending = 0;
let queue = Promise.resolve();
const lock = (on) => document.documentElement.classList.toggle('nv-taies-sync', on);
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/* Une remise en ordre à la fois ; le verrou tombe quand plus rien n'attend et que le panier a eu le temps de se redessiner */
function schedule() {
  queue = queue.then(sync).catch(() => {}).then(() => wait(150)).then(() => {
    pending = Math.max(0, pending - 1);
    if (!pending) lock(false);
  });
}

function hold() {
  pending += 1;
  lock(true);
}

if (data && data.variantes) {
  eventsModule.then((events) => {
    const name = (events && events.StandardEvents && events.StandardEvents.cartLinesUpdate) || 'cart:lines-update';
    document.addEventListener(name, (event) => {
      if (event.nuveaTaies || !event.promise) return;
      /* Figé dès le clic : la remise en ordre ne part qu'une fois la mise à jour d'Horizon terminée */
      hold();
      event.promise.then(schedule, schedule);
    });
  });

  /* Envoi vers le paiement pendant une remise en ordre : on le retient, puis on le relance avec le panier à jour */
  document.addEventListener('submit', (event) => {
    if (!pending) return;
    const form = event.target;
    /* Seul le formulaire du panier (action /cart) : l'ajout depuis la fiche (/cart/add) n'est pas concerné */
    if (!(form instanceof HTMLFormElement) || !new URL(form.action, window.location.href).pathname.replace(/\/$/, '').endsWith('/cart')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const formId = form.id;
    const submitterName = event.submitter && event.submitter.name;
    queue.then(() => wait(200)).then(() => {
      const target = form.isConnected ? form : document.getElementById(formId);
      if (!target) return;
      const submitter = submitterName
        ? [...document.querySelectorAll('[name="' + submitterName + '"]')].find((b) => b.form === target)
        : undefined;
      target.requestSubmit(submitter);
    });
  }, true);

  /* Panier déjà rempli en arrivant (ancien panier, retour en arrière) : on remet les taies en ordre */
  if (data.articles > 0) {
    hold();
    schedule();
  }
}
